import {
  DEFAULT_OPTIN_AFTER_PAGES,
  KINETIZINE_PRIVACY_URL,
  OPTIN_MODES,
} from "../constants/optInDefaults";
import { isAcceptAll } from "./cookieConsentUtils";
import { getOptinCookie, getReceiverCookie } from "./helper";

const pagesKey = (token) => `optin_pages_${token}`;
const triggerKey = (token) => `optin_trigger_page_${token}`;
const skippedKey = (token) => `optin_skipped_${token}`;
const completedKey = (token) => `optin_completed_${token}`;

const readList = (storage, key) => {
  try {
    const parsed = JSON.parse(storage.getItem(key) || "[]");
    return Array.isArray(parsed) ? parsed.map(Number).filter(Number.isFinite) : [];
  } catch (error) {
    return [];
  }
};

const writeList = (storage, key, list) => {
  try {
    storage.setItem(key, JSON.stringify(list));
  } catch (error) {
    return;
  }
};

export const getOptInMode = (tlb) => {
  const mode = tlb?.opt_in?.optin_mode;
  if (Object.values(OPTIN_MODES).includes(mode)) return mode;
  return tlb?.optin_2nd_page_viewed ? OPTIN_MODES.SECOND_PAGE : OPTIN_MODES.PAGE_NUMBER;
};

export const getOptInAfterPages = (tlb) => {
  const value = Number(tlb?.opt_in?.optin_after_pages);
  return Number.isFinite(value) && value >= 1 ? value : DEFAULT_OPTIN_AFTER_PAGES;
};

export const getViewedPages = (token) => {
  if (!token) return [];
  const pages = new Set(readList(sessionStorage, pagesKey(token)));
  if (isAcceptAll()) readList(localStorage, pagesKey(token)).forEach((page) => pages.add(page));
  return Array.from(pages);
};

export const recordViewedPage = (token, page) => {
  if (!token || !Number.isFinite(Number(page)) || Number(page) < 1) return getViewedPages(token);
  const pages = getViewedPages(token);
  if (!pages.includes(Number(page))) pages.push(Number(page));
  writeList(sessionStorage, pagesKey(token), pages);
  if (isAcceptAll()) writeList(localStorage, pagesKey(token), pages);
  return pages;
};

export const getOptInTriggerPage = (token) => {
  if (!token) return null;
  try {
    const value = Number(sessionStorage.getItem(triggerKey(token)));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch (error) {
    return null;
  }
};

export const setOptInTriggerPage = (token, page) => {
  if (!token) return;
  try {
    sessionStorage.setItem(triggerKey(token), String(page));
  } catch (error) {
    return;
  }
};

export const markOptInSkipped = (token) => {
  if (!token) return;
  try {
    sessionStorage.setItem(skippedKey(token), "true");
  } catch (error) {
    return;
  }
};

export const isOptInSkipped = (token) => {
  if (!token) return false;
  try {
    return sessionStorage.getItem(skippedKey(token)) === "true";
  } catch (error) {
    return false;
  }
};

export const isOptInResolved = (token) => {
  if (!token) return false;
  const receiverCookie = getReceiverCookie();
  const optinCookie = getOptinCookie();
  let completed = false;
  try {
    completed = sessionStorage.getItem(completedKey(token)) === "true";
  } catch (error) {
    completed = false;
  }
  return (
    completed ||
    receiverCookie[token] !== undefined ||
    optinCookie[token]?.show === "none" ||
    isOptInSkipped(token)
  );
};

const pagesWithOutgoingRoutes = () => {
  const elements = window?.objInit?.elements;
  if (!elements || typeof elements !== "object") return new Set();
  const candidates = Object.values(elements).flatMap((value) =>
    value && typeof value === "object" && !value.question_title && value.page == null
      ? Object.values(value)
      : [value]
  );
  const pages = new Set();
  candidates.forEach((el) => {
    if (!el || el.page == null) return;
    const hasNavigator =
      el.question_title?.status === "approved" && el.question_title?.enabled !== false;
    const hasPageLink =
      Number(el.value?.page_link) > 0 || /data-page-link/.test(el.value?.html_text || "");
    if (hasNavigator || hasPageLink) pages.add(Number(el.page));
  });
  return pages;
};

export const isRouteEndPage = (tlb, page, totalPages) => {
  if (Number(totalPages) && Number(page) === Number(totalPages)) return true;
  if (tlb?.cyoa_enabled !== true) return false;
  return !pagesWithOutgoingRoutes().has(Number(page));
};

export const isGuideTriggerPage = (tlb, page, totalPages) => {
  if (isRouteEndPage(tlb, page, totalPages)) return true;
  return tlb?.guide_trigger === "page" && Number(tlb?.guide_trigger_page) > 0 && Number(page) === Number(tlb.guide_trigger_page);
};

export const optInTemplateValues = (tlb, extra = {}) => ({
  sender_first_name: tlb?.first_name || tlb?.username || "Your contact",
  sender_company:
    tlb?.company_name ||
    tlb?.user_company_name ||
    tlb?.tracking_link_company?.name ||
    tlb?.first_name ||
    "the sender",
  ...extra,
});

export const fillOptInTemplate = (text, values) =>
  String(text ?? "").replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (match, key) =>
    values[key] != null && values[key] !== "" ? values[key] : match
  );

export const resolvePrivacyUrl = (url) => {
  const value = String(url ?? "").trim();
  return /^https?:\/\//i.test(value) ? value : KINETIZINE_PRIVACY_URL;
};

export const getOptInPrivacyUrl = (tlb) => resolvePrivacyUrl(tlb?.company_privay_url);
